import React, { useEffect, useState } from 'react';
import { TextInput, Spinner } from 'flowbite-react';
import { useFusionAuth } from '@fusionauth/react-sdk';
import { toast } from 'react-toastify';
import Title from '../../components/Title';
import { DraftSummary, listDraftsByEmail } from '../../api/asset-management';
import DraftRow from './DraftRow';

const AssetManagement: React.FC = () => {
  const { user } = useFusionAuth();
  const operatorEmail = user?.email || '';

  const [email, setEmail] = useState('');
  const [debouncedEmail, setDebouncedEmail] = useState('');
  const [drafts, setDrafts] = useState<DraftSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedEmail(email.trim()), 500);
    return () => clearTimeout(timer);
  }, [email]);

  useEffect(() => {
    if (!debouncedEmail || !debouncedEmail.includes('@')) {
      setDrafts([]);
      setError(null);
      return;
    }
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const result = await listDraftsByEmail(debouncedEmail, operatorEmail);
        if (!cancelled) setDrafts(result || []);
      } catch (err) {
        if (cancelled) return;
        const axiosErr = err as {
          response?: { data?: { message?: string } };
          message?: string;
        };
        const msg =
          axiosErr?.response?.data?.message ||
          axiosErr?.message ||
          'Failed to load drafts.';
        setError(msg);
        setDrafts([]);
        toast.error(msg);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [debouncedEmail, operatorEmail]);

  return (
    <div className="p-6 space-y-4">
      <Title>Asset Management</Title>
      <p className="text-sm text-gray-600">
        Look up a customer by email and trigger cover/manuscript PDF generation
        without going through the order flow.
      </p>

      <div className="max-w-md">
        <TextInput
          type="email"
          placeholder="customer@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
      </div>

      {loading && (
        <div className="flex items-center text-sm text-gray-600">
          <Spinner size="sm" className="mr-2" />
          Loading drafts…
        </div>
      )}

      {error && !loading && (
        <div className="text-sm text-red-600">{error}</div>
      )}

      {!loading && !error && debouncedEmail && drafts.length === 0 && (
        <div className="text-sm text-gray-500">
          No active drafts for that email.
        </div>
      )}

      {drafts.length > 0 && (
        <div className="overflow-x-auto border border-gray-200 rounded">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Thumbnail
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Draft Name
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Created
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Cover
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Cover Generated
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Action
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {drafts.map((d) => (
                <DraftRow
                  key={d.draftId}
                  draft={d}
                  operatorEmail={operatorEmail}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AssetManagement;
